import React, { useState, useEffect, useCallback } from 'react';
import { getApprovalCandidates } from 'Api/apiService';
import {
    ApproverLoadingStyled,
} from './HumanEditorV2.styles';
import { SbSelect, SbSpin, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import {
    ASSIGN_APPROVER, getAssignApproverState, getLockedApprover, isOutOfScopeError,
} from 'Utils/sendAndPublish';

const APPROVER_TOOLTIP =
    "Choose the HR Manager who will review and approve this JD. They will be notified immediately after submission.";

// Props match the call site in SbJobDetailsV2:
//   selectedApprover    — controlled value (email string | null) from page-level state
//   setSelectedApprover — page-level state setter
//   assignedApproverName — previous approver email to pre-select on load
//   jobId               — the JD (its job_id): the server lists only approvers who have it in their view scope
//   reassignedApproverEmail — a review an HR Admin reassigned: pre-set to that approver and locked (part 5)
const ApproverSelectorV2 = ({ selectedApprover, setSelectedApprover, assignedApproverName, jobId,
    reassignedApproverEmail = null }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [outOfScope, setOutOfScope] = useState(false);

    // One list per JD: a new jobId starts over (earlier answers are ignored), and nothing is asked before the JD is
    // known (the unscoped list is never fetched from here)
    useEffect(() => {
        if (!jobId) return undefined;
        let cancelled = false;
        setLoading(true);
        setLoadFailed(false);
        setOutOfScope(false);
        setOptions([]);
        getApprovalCandidates(jobId)
            .then((data) => {
                if (cancelled) return;
                const formatted = (Array.isArray(data) ? data : []).map((c) => ({ value: c.value, label: c.label }));
                setOptions(formatted);
                // A reassigned review: its approver, locked (or nobody, when they can't approve it now)
                const locked = getLockedApprover(formatted, reassignedApproverEmail);
                if (locked && typeof setSelectedApprover === 'function') {
                    setSelectedApprover(locked.selected);
                } else if (typeof setSelectedApprover === 'function'
                    && !(selectedApprover && formatted.some((opt) => opt.value === selectedApprover))) {
                    // Keep a pick still on the list; otherwise pre-select the previous approver if listed, else clear it
                    const match = assignedApproverName && formatted.find((opt) => opt.value === assignedApproverName);
                    setSelectedApprover(match ? match.value : null);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load approvers', err);
                setLoadFailed(true);
                setOutOfScope(isOutOfScopeError(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = useCallback((value) => {
        if (typeof setSelectedApprover === 'function') {
            setSelectedApprover(value || null);
        }
    }, [setSelectedApprover]);

    if (loading) {
        return (
            <ApproverLoadingStyled>
                <SbSpin size="small" />
                <span>Loading approvers...</span>
            </ApproverLoadingStyled>
        );
    }

    // Nobody to pick (none has the JD in their view scope), or the list failed: the same message as the Send page;
    // a reassigned review whose approver can't approve it now: ask an HR Admin
    const locked = getLockedApprover(options, reassignedApproverEmail);
    const { message: listMessage } = getAssignApproverState({ options, selected: selectedApprover, loading, loadFailed,
        outOfScope });
    const message = listMessage || (locked?.unavailable ? locked.note : null);
    if (message) {
        return (
            <>
              Assign Approver
              <span data-testid="approver-unavailable">{message}</span>
            </>
        );
    }

    return (
            <>
              Assign Approver
              <SbTooltip content={APPROVER_TOOLTIP}>
                <TooltipTriggerStyled>
                  <SbSelect
                      options={options}
                      value={selectedApprover || undefined}
                      onChange={handleChange}
                      placeholder={ASSIGN_APPROVER.placeholder}
                      showSearch
                      optionFilterProp="label"
                      allowClear={!locked}
                      disabled={!!locked}
                      placement="bottomLeft"
                      getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </TooltipTriggerStyled>
              </SbTooltip>
              {locked && <span data-testid="approver-reassigned">{locked.note}</span>}
            </>
    );
};

export default ApproverSelectorV2;
